import { CYBERSOURCE_COMPANY } from './constants';

// Validation regex patterns grouped for clarity
const REGEX = {
  NAME_WITH_SPACES: /^[A-Za-z\s]+$/,
  EMAIL: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  PHONE: /^[0-9]{10}$/,
  GATEWAY: /^[0-9]{7}$/,
};

/**
 * Field validation rules and error messages.
 * Each field can have:
 * - required: error message if empty
 * - pattern: { regex, message } for pattern validation
 * - custom: function for custom validation logic
 */
const fieldRules = {
  firstName: {
    required: "First name is required",
    pattern: {
      regex: REGEX.NAME_WITH_SPACES,
      message: "First name can only contain letters",
    },
    custom: (value) => {
      if (!value) return undefined;
      const formatted = value.replace(/\s+/g, " ").trim();
      if (formatted.length < 2) return "First name must be at least 2 characters long";
      return undefined;
    },
  },
  lastName: {
    required: "Last name is required",
    pattern: {
      regex: REGEX.NAME_WITH_SPACES,
      message: "Last name can only contain letters",
    },
    custom: (value) => {
      if (!value) return undefined;
      const formatted = value.replace(/\s+/g, " ").trim();
      if (formatted.length < 2) return "Last name must be at least 2 characters long";
      return undefined;
    },
  },
  email: {
    required: "Email is required",
    pattern: {
      regex: REGEX.EMAIL,
      message: "Please enter a valid email address",
    },
  },
  phoneNumber: {
    required: "Phone number is required",
    custom: (value) => {
      if (!value) return undefined;
      // Remove all non-numeric characters (spaces, dashes, parentheses, dots, etc)
      const numbersOnly = value.replace(/[^0-9]/g, "");
      // Limit to 10 digits if longer
      if (numbersOnly.length > 10)
        return "Phone number cannot be more than 10 digits";
      if (numbersOnly.length < 10)
        return "Phone number must be exactly 10 digits";
      if (!REGEX.PHONE.test(numbersOnly))
        return "Phone number can only contain digits (0-9)";
      return undefined;
    },
  },
  Gateway: {
    required: "Please enter your 7-digit Gateway number",
    custom: (value) => {
      if (!value) return undefined;
      if (!REGEX.GATEWAY.test(value))
        return "Gateway number must be exactly 7 digits and contain only digits (0-9)";
      return undefined;
    },
  },
  salesRep: {
    required: "Sales Representative is required",
    custom: (value) =>
      !value || value === "Select Sales Rep"
        ? "Sales Representative is required"
        : undefined,
  },
  notes: {
    custom: (value) => {
      if (value && value.length > 2000) {
        return "Notes cannot exceed 2000 characters.";
      }
      return undefined;
    },
  },
  reason: {
    custom: (value) => {
      if (value && value.length > 255) {
        return "Reason cannot exceed 255 characters.";
      }
      return undefined;
    },
  },
};

/**
 * Validates all fields in the state object according to fieldRules.
 * @param {Object} state - The form state with field values.
 * @param {Object} config - Additional config (e.g., { hasGateway: true/false }).
 * @returns {Object} errors - Object with field names as keys and error messages as values.
 */
export const validateFields = (state, config) => {
  const errors = {};

  for (const field in fieldRules) {
    // Skip Gateway if not required by config
    if (field === "Gateway" && config && !config.hasGateway) continue;
    // Skip salesRep validation for simple form, preferred partner, or when CyberSource is selected
    if (field === "salesRep" && (config.hasSimpleForm || config.title === "Preferred" || state.company === CYBERSOURCE_COMPANY)) continue;
    // Skip notes and reason if not present in the form state
    if ((field === "notes" || field === "reason") && !(field in state)) continue;

    const value = state[field];
    const rules = fieldRules[field];

    // Required check
    if (rules.required && (!value || value === "")) {
      errors[field] = rules.required;
      continue;
    }

    // Pattern check
    if (rules.pattern && value && !rules.pattern.regex.test(value)) {
      errors[field] = rules.pattern.message;
      continue;
    }

    // Custom validation
    if (rules.custom) {
      const customError = rules.custom(value);
      if (customError) {
        errors[field] = customError;
      }
    }
  }

  return errors;
};

/**
 * Validates a single field by name and value.
 * @param {string} name - Field name.
 * @param {string} value - Field value.
 * @param {Object} state - (Optional) Full form state.
 * @param {Object} config - (Optional) Config object.
 * @returns {string|undefined} Error message or undefined if valid.
 */
export const validateField = (name, value, state, config) => {
  // Skip Gateway if not required by config
  if (name === "Gateway" && config && !config.hasGateway) return undefined;
  // Skip salesRep validation for simple form, preferred partner, or when CyberSource is selected
  if (name === "salesRep" && (config.hasSimpleForm || config.currentPartner === "Preferred" || (state && state.company === CYBERSOURCE_COMPANY))) return undefined;
  // Skip notes and reason if not present in the form state
  if ((name === "notes" || name === "reason") && state && !(name in state)) return undefined;

  const rules = fieldRules[name];
  if (!rules) return undefined;

  // Required check
  if (rules.required && (!value || value === "")) {
    return rules.required;
  }

  // Pattern check
  if (rules.pattern && value && !rules.pattern.regex.test(value)) {
    return rules.pattern.message;
  }

  // Custom validation
  if (rules.custom) {
    return rules.custom(value);
  }

  return undefined;
};
